import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown, ChevronLeft, ChevronUp, Loader2 } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { serviceRequestApiService } from '@/lib/api/serviceRequest.service';
import type { FinalTerms, PostAcceptCondition } from '@/lib/api/serviceRequest.service';
import type { ProposalRound, ServiceRequest, ServiceRequestVendor } from '@om/shared-types';


function RoundCard({ round, isLatest }: { round: ProposalRound; isLatest: boolean }) {
  const { t } = useTranslation(['services']);
  const isVendor = round.submitted_by === 'vendor';
  const roundLabel = t(`services:proposalDetail.roundLabel.${round.submitted_by}`, { defaultValue: round.submitted_by });
  return (    <div
      className={`rounded-lg border p-4 ${
        isLatest
          ? isVendor
            ? 'border-purple-200 bg-purple-50'
            : 'border-blue-200 bg-blue-50'
          : 'border-gray-200 bg-white opacity-70'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
              isVendor ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
            }`}
          >
            {roundLabel}
          </span>
          <span className="text-xs text-gray-500">{t('services:proposalDetail.roundNumber', { round: round.round })}</span>
          {isLatest && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
              {t('services:proposalDetail.latest')}
            </span>
          )}
        </div>
        <p className="text-base font-semibold text-gray-900">₹{round.amount.toLocaleString()}</p>
      </div>
      {round.scope_summary && (
        <p className="mt-2 text-sm text-gray-700">{round.scope_summary}</p>
      )}
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
        {round.terms && <span>{t('services:proposalDetail.terms', { terms: round.terms })}</span>}
        {round.valid_until && (
          <span>{t('services:proposalDetail.validUntil', { date: new Date(round.valid_until).toLocaleDateString() })}</span>
        )}
        {round.submitted_at && (
          <span>{t('services:proposalDetail.submitted', { date: new Date(round.submitted_at).toLocaleDateString() })}</span>
        )}
      </div>
    </div>
  );
}

export function ProposalDetailPage() {
  const { t } = useTranslation(['services']);
  const { celebrationId, serviceId, requestId, vendorId } = useParams<{
    celebrationId: string;
    serviceId: string;
    requestId: string;
    vendorId: string;
  }>();
  const navigate = useNavigate();
  const cid = Number(celebrationId);
  const sid = Number(serviceId);
  const rid = Number(requestId);
  const vid = Number(vendorId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const [vendor, setVendor] = useState<ServiceRequestVendor | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCounterForm, setShowCounterForm] = useState(false);
  const [finalTerms, setFinalTerms] = useState<FinalTerms | null>(null);
  const [finalTermsLoading, setFinalTermsLoading] = useState(false);
  const [finalizationBusy, setFinalizationBusy] = useState(false);
  const [conditionType, setConditionType] = useState<PostAcceptCondition['condition_type']>('document');
  const [conditionDescription, setConditionDescription] = useState('');

  // Counter-offer form state
  const [counterAmount, setCounterAmount] = useState('');
  const [counterScope, setCounterScope] = useState('');
  const [counterTerms, setCounterTerms] = useState('');
  const [counterValidUntil, setCounterValidUntil] = useState('');
  const [isRequestCopyExpanded, setIsRequestCopyExpanded] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [requestDetail, vendors] = await Promise.all([
        serviceRequestApiService.getRequest(cid, rid),
        serviceRequestApiService.listVendors(cid, rid),
      ]);
      setRequest(requestDetail);
      const found = vendors.find(v => v.vendor_id === vid) ?? null;
      setVendor(found);

      const canLoadFinalTerms = ['under_review', 'closed', 'awarded', 'signed'].includes(requestDetail.status);
      if (!canLoadFinalTerms) {
        setFinalTerms(null);
        setFinalTermsLoading(false);
      } else {
        setFinalTermsLoading(true);
        try {
          const terms = await serviceRequestApiService.getFinalTerms(cid, rid);
          setFinalTerms(terms);
        } catch {
          setFinalTerms(null);
        } finally {
          setFinalTermsLoading(false);
        }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load proposal');
    } finally {
      setLoading(false);
    }
  }, [cid, rid, vid]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (finalTerms?.accepted_quote?.id) {
      setShowCounterForm(false);
    }
  }, [finalTerms?.accepted_quote?.id]);

  const handleAward = async () => {
    setActionError(null);
    try {
      setSubmitting(true);
      const updated = await serviceRequestApiService.transitionVendorStatus(cid, rid, vid, 'awarded');
      setVendor(updated);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to award');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setActionError(null);
    try {
      setSubmitting(true);
      const updated = await serviceRequestApiService.transitionVendorStatus(cid, rid, vid, 'rejected');
      setVendor(updated);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCounter = async () => {
    if (!counterAmount || isNaN(Number(counterAmount))) {
      setActionError('A valid amount is required for the counter-offer');
      return;
    }
    if (!counterScope.trim()) {
      setActionError('Scope summary is required for the counter-offer');
      return;
    }
    if (!counterValidUntil.trim()) {
      setActionError('Valid-until date is required for the counter-offer');
      return;
    }
    setActionError(null);
    try {
      setSubmitting(true);
      const updated = await serviceRequestApiService.submitProposal(cid, rid, vid, {
        amount: Number(counterAmount),
        scope_summary: counterScope.trim(),
        terms: counterTerms.trim(),
        valid_until: counterValidUntil,
      });
      setVendor(updated as unknown as ServiceRequestVendor);
      setShowCounterForm(false);
      setCounterAmount('');
      setCounterScope('');
      setCounterTerms('');
      setCounterValidUntil('');
    } catch (e: unknown) {
      console.error('Counter-offer submit failed:', e);
      setActionError(e instanceof Error ? e.message : 'Failed to submit counter-offer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCondition = async () => {
    if (!conditionDescription.trim()) {
      setActionError('Condition description is required');
      return;
    }

    setActionError(null);
    setFinalizationBusy(true);
    try {
      await serviceRequestApiService.createPostAcceptCondition(cid, rid, {
        condition_type: conditionType,
        description: conditionDescription.trim(),
      });
      setConditionDescription('');
      await loadData();
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to add condition');
    } finally {
      setFinalizationBusy(false);
    }
  };

  const handleResolveCondition = async (conditionId: number, status: 'satisfied' | 'waived') => {
    setActionError(null);
    setFinalizationBusy(true);
    try {
      await serviceRequestApiService.resolvePostAcceptCondition(cid, rid, conditionId, status);
      await loadData();
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to resolve condition');
    } finally {
      setFinalizationBusy(false);
    }
  };

  const handleFinalizeAward = async () => {
    if (!finalTerms?.accepted_quote?.id) {
      setActionError('No accepted quote is available for award finalization');
      return;
    }

    setActionError(null);
    setFinalizationBusy(true);
    try {
      const result = await serviceRequestApiService.awardVendor(cid, rid, {
        accepted_quote_id: finalTerms.accepted_quote.id,
      });
      navigate(`/celebrations/${cid}/bookings/${result.booking.id}`);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to finalize award');
    } finally {
      setFinalizationBusy(false);
    }
  };

  const handleFinalizeSigning = async () => {
    setActionError(null);
    setFinalizationBusy(true);
    try {
      const result = await serviceRequestApiService.signServiceRequest(cid, rid);
      navigate(`/celebrations/${cid}/bookings/${result.booking.id}`);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to complete signing');
    } finally {
      setFinalizationBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-500">{t('services:proposalDetail.loading')}</span>
      </div>
    );
  }

  if (!vendor) {
    return (
      <PageContainer width="wide">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error ?? t('services:proposalDetail.vendorNotFound')}
        </div>
      </PageContainer>
    );
  }

  const rounds = vendor.proposals ?? [];
  const latestRound = rounds.length > 0 ? rounds[rounds.length - 1] : null;
  const isQuoted = vendor.status === 'quoted';
  const isAwarded = vendor.status === 'awarded';
  const isRejected = vendor.status === 'rejected';
  const acceptedQuoteForVendor = finalTerms?.accepted_quote?.vendor_id === vendor.vendor_id;
  const hasAcceptedQuote = Boolean(finalTerms?.accepted_quote?.id);
  const requestCopy = request?.posting_content?.trim() || request?.description?.trim() || 'No request copy available.';

  return (
    <PageContainer width="wide" className="space-y-5">
      <AppBreadcrumbs />

      {(error || actionError) && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {actionError ?? error}
          <button type="button" onClick={() => setActionError(null)} className="ml-auto text-xs underline">
            {t('services:proposalDetail.dismiss')}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">
          {vendor.vendor_name ?? `Vendor #${vendor.vendor_id}`} — {t('services:proposalDetail.vendorRequest')}
        </h1>
        <p className="mt-0.5 text-sm text-gray-500">
          {t(rounds.length !== 1 ? 'services:proposalDetail.rounds_other' : 'services:proposalDetail.rounds', { count: rounds.length })} · {t('services:proposalDetail.status')}:{' '}
          <span className="font-medium capitalize">{vendor.status}</span>
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{t('services:proposalDetail.requestSentToVendor')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {request?.request_name ?? t('services:proposalDetail.serviceRequest')}
              {request?.service_name ? ` · ${request.service_name}` : ''}
            </p>
          </div>
          <div className="text-right text-xs text-gray-500">
            <p>{t('services:proposalDetail.sent')}: {vendor.invited_at ? new Date(vendor.invited_at).toLocaleDateString() : '—'}</p>
            <p>{t('services:proposalDetail.viewed')}: {vendor.viewed_at ? new Date(vendor.viewed_at).toLocaleDateString() : t('services:proposalDetail.notYetViewed')}</p>
          </div>
        </div>

        <dl className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('services:proposalDetail.budget')}</dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {request?.budget != null ? `₹${request.budget.toLocaleString()}` : t('services:proposalDetail.notSet')}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('services:proposalDetail.priority')}</dt>
            <dd className="mt-0.5 text-sm capitalize text-gray-900">{request?.priority?.replace('_', ' ') ?? t('services:proposalDetail.notSet')}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('services:proposalDetail.vendorRequestCopy')}</dt>
            <dd className="mt-1">
              <button
                type="button"
                onClick={() => setIsRequestCopyExpanded((prev) => !prev)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              >
                {isRequestCopyExpanded ? t('services:proposalDetail.collapseRequestCopy') : t('services:proposalDetail.expandRequestCopy')}
                {isRequestCopyExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {isRequestCopyExpanded && (
                <div className="mt-2 whitespace-pre-wrap rounded-md border border-gray-100 bg-gray-50 px-3 py-3 text-sm text-gray-900">
                  {requestCopy}
                </div>
              )}
            </dd>
          </div>
        </dl>
      </div>

      {/* Proposal Timeline */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold text-gray-900">{t('services:proposalDetail.proposalHistory')}</h2>
        {rounds.length === 0 ? (
          <p className="text-sm text-gray-500">{t('services:proposalDetail.noProposalYet')}</p>
        ) : (
          <div className="space-y-3">
            {[...rounds].reverse().map((round, idx) => (
              <RoundCard
                key={`${round.round}-${round.submitted_by}`}
                round={round}
                isLatest={idx === 0}
              />
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      {!isRejected && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-gray-900">{t('services:proposalDetail.actions')}</h2>
          <div className="flex flex-wrap gap-3">
            {!isQuoted && !isAwarded && (
              <button
                type="button"
                onClick={handleReject}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('services:proposalDetail.reject')}
              </button>
            )}
            {isQuoted && !showCounterForm && !hasAcceptedQuote && (
              <>
                <button
                  type="button"
                  onClick={handleAward}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Award this Vendor
                </button>
                <button
                  type="button"
                  onClick={() => setShowCounterForm(true)}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                >
                  Counter Offer
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-50"
                >
                  {t('services:proposalDetail.reject')}
                </button>
              </>
            )}
            {isQuoted && hasAcceptedQuote && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t('services:proposalDetail.acceptedQuoteLocked')}
              </div>
            )}
            {isAwarded && (
              <button
                type="button"
                onClick={() => navigate(`/celebrations/${cid}/bookings`)}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
              >
                {t('services:proposalDetail.createBooking')}
              </button>
            )}
          </div>

          {/* Counter-offer form */}
          {showCounterForm && !hasAcceptedQuote && (
            <div className="mt-4 space-y-3 rounded-lg border border-blue-100 bg-blue-50 p-4">
              <h3 className="text-sm font-semibold text-blue-900">{t('services:proposalDetail.submitCounterOffer')}</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">{t('services:proposalDetail.amountLabel')}</label>
                  <input
                    type="number"
                    min={0}
                    placeholder={latestRound ? String(latestRound.amount) : '0'}
                    value={counterAmount}
                    onChange={e => setCounterAmount(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">{t('services:proposalDetail.validUntilLabel')}</label>
                  <input
                    type="date"
                    aria-label={t('services:proposalDetail.validUntilAriaLabel')}
                    value={counterValidUntil}
                    onChange={e => setCounterValidUntil(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-700">{t('services:proposalDetail.scopeSummaryLabel')}</label>
                  <textarea
                    rows={2}
                    value={counterScope}
                    onChange={e => setCounterScope(e.target.value)}
                    placeholder={t('services:proposalDetail.scopeSummaryPlaceholder')}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-700">{t('services:proposalDetail.termsLabel')}</label>
                  <input
                    type="text"
                    value={counterTerms}
                    onChange={e => setCounterTerms(e.target.value)}
                    placeholder={t('services:proposalDetail.termsPlaceholder')}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              {actionError && (
                <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {actionError}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSubmitCounter}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t('services:proposalDetail.submitCounter')}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCounterForm(false); setActionError(null); }}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('services:proposalDetail.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Finalization Journey */}
      {!isRejected && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{t('services:proposalDetail.finalTermsTitle')}</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {t('services:proposalDetail.finalTermsSubtitle')}
              </p>
            </div>
            {finalTermsLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>

          {!finalTerms && !finalTermsLoading && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t('services:proposalDetail.finalizationNotAvailable')}
            </div>
          )}

          {finalTerms && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">{t('services:proposalDetail.acceptedQuote')}</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {finalTerms.accepted_quote.quote_number} · ₹{finalTerms.accepted_quote.total_amount.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">{t('services:proposalDetail.conditions')}</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {t('services:proposalDetail.conditionsSummary', { satisfied: finalTerms.conditions_summary.satisfied, pending: finalTerms.conditions_summary.pending })}
                  </p>
                </div>
                <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">{t('services:proposalDetail.requestStatus')}</p>
                  <p className="mt-1 text-sm font-medium capitalize text-gray-900">{request?.status?.replace('_', ' ')}</p>
                </div>
              </div>

              {finalTerms.conditions.length > 0 ? (
                <div className="space-y-2">
                  {finalTerms.conditions.map((condition) => {
                    const isClosed = condition.status !== 'pending';
                    return (
                      <div key={condition.id} className="rounded-md border border-gray-100 px-3 py-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-900">{condition.description}</p>
                            <p className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-500">
                              {condition.condition_type} · {condition.status}
                            </p>
                          </div>
                          {!isClosed && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleResolveCondition(condition.id, 'satisfied')}
                                disabled={finalizationBusy}
                                className="rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                              >
                                {t('services:proposalDetail.markSatisfied')}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleResolveCondition(condition.id, 'waived')}
                                disabled={finalizationBusy}
                                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                {t('services:proposalDetail.waive')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-500">{t('services:proposalDetail.noConditions')}</p>
              )}

              <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">{t('services:proposalDetail.addPostAcceptCondition')}</p>
                <div className="grid gap-2 sm:grid-cols-[180px,1fr]">
                  <select
                    aria-label={t('services:proposalDetail.conditionTypePlaceholder')}
                    value={conditionType}
                    onChange={(e) => setConditionType(e.target.value as PostAcceptCondition['condition_type'])}
                    className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-900"
                  >
                    <option value="document">{t('services:proposalDetail.conditionTypes.document')}</option>
                    <option value="tasting">{t('services:proposalDetail.conditionTypes.tasting')}</option>
                    <option value="approval">{t('services:proposalDetail.conditionTypes.approval')}</option>
                    <option value="insurance">{t('services:proposalDetail.conditionTypes.insurance')}</option>
                    <option value="deposit">{t('services:proposalDetail.conditionTypes.deposit')}</option>
                    <option value="other">{t('services:proposalDetail.conditionTypes.other')}</option>
                  </select>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={conditionDescription}
                      onChange={(e) => setConditionDescription(e.target.value)}
                      placeholder={t('services:proposalDetail.conditionDescriptionPlaceholder')}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-900"
                    />
                    <button
                      type="button"
                      onClick={handleCreateCondition}
                      disabled={finalizationBusy}
                      className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('services:proposalDetail.add')}
                    </button>
                  </div>
                </div>
              </div>

              {acceptedQuoteForVendor && (
                <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    onClick={handleFinalizeAward}
                    disabled={finalizationBusy || !finalTerms.ready_for_award}
                    className="rounded-md bg-primary px-3 py-2 text-xs font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {finalizationBusy ? t('services:proposalDetail.processing') : t('services:proposalDetail.finalizeAward')}
                  </button>
                  <button
                    type="button"
                    onClick={handleFinalizeSigning}
                    disabled={finalizationBusy || !finalTerms.ready_for_signing}
                    className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {finalizationBusy ? t('services:proposalDetail.processing') : t('services:proposalDetail.completeSigning')}
                  </button>
                </div>
              )}

              {!acceptedQuoteForVendor && (
                <p className="text-xs text-amber-700">
                  {t('services:proposalDetail.finalizationNotForVendor')}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}`)}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ChevronLeft className="h-4 w-4" />
        {t('services:proposalDetail.backToRequest')}
      </button>
    </PageContainer>
  );
}
